import React, { useEffect, useMemo, useState } from "react";
import {
  AlertReason,
  InterventionGroup,
  InterventionGroupStage,
} from "../../../../../../../store/onboarding/cases/types";
import PreparationStageInfo from "../PreparationStageInfo";
import ImplementationStageInfo from "../ImplementationStageInfo";
import MonitoringStageInfo from "../MonitoringStageInfo";
import useUserRole from "../../../../../../../utils/hooks/useUserRole";
import { useDispatch, useSelector } from "react-redux";
import {
  changeSelectedInterventionGroup,
  patchInterventionGroup,
  showInterventionPlanModal,
} from "../../../../../../../store/onboarding/cases/actions";
import { MeetingType } from "../../../../../../../store/onboarding/meeting-module/types";
import { useHistory } from "react-router";
import { linkToMeetingTab } from "../../../../../../../store/onboarding/meeting-module/actions";
import LoadingIndicator from "../../../../../../common/onboarding/LoadingIndicator";
import { Student } from "../../../../../../../store/onboarding/types";
import { ApplicationState } from "../../../../../../../store";
import { DataFilterState } from "../../../../../../../store/dataFilter/types";

const useInterventionGroupItem = ({
  interventionGroup,
  showArchivedGroups,
  isReadonly,
  hideChartActions,
  isIntGrpIDsWithReadAccess,
  progressOverviewLoader,
  caseId,
  student,
}: {
  interventionGroup: InterventionGroup;
  showArchivedGroups?: boolean;
  isReadonly?: boolean;
  hideChartActions?: boolean;
  isIntGrpIDsWithReadAccess?: boolean;
  progressOverviewLoader?: boolean;
  caseId?: number;
  student?: Student; // Assuming student is an object, adjust type as necessary
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [stage, setStage] = useState<InterventionGroupStage>(
    interventionGroup.stage || InterventionGroupStage.STAGE_PREPARATION
  );

  const { exitedStudentsGroup } = useSelector<
    ApplicationState,
    DataFilterState
  >((s) => s.dataFilter);

  useEffect(() => {
    if (showArchivedGroups || isReadonly || isIntGrpIDsWithReadAccess) {
      setStage(InterventionGroupStage.STAGE_MONITORING);
    } else {
      if (interventionGroup.skipped_meeting) {
        setStage(InterventionGroupStage.STAGE_MONITORING);
        return;
      }
      if (interventionGroup.skipped_prep) {
        setStage(InterventionGroupStage.STAGE_IMPLEMENTATION);
        return;
      }
      if (interventionGroup.latest_meeting) {
        if (
          interventionGroup.latest_meeting.planning_notes &&
          interventionGroup.latest_meeting.meeting_notes
        ) {
          setStage(InterventionGroupStage.STAGE_MONITORING);
        } else if (interventionGroup.latest_meeting.planning_notes) {
          setStage(InterventionGroupStage.STAGE_IMPLEMENTATION);
        } else {
          setStage(InterventionGroupStage.STAGE_PREPARATION);
        }
      }
    }
  }, [interventionGroup, showArchivedGroups, isReadonly]);

  const hasAlert = useMemo(
    () => {
      let isShowAlertForStudent = true;
      if (student && interventionGroup.alert_reason) {
        if(interventionGroup.alert_reason.includes(AlertReason.MetGoal)) {
          isShowAlertForStudent =  interventionGroup.student_goals?.some(
            (goal) => (goal.student.id === student.id) && goal.is_met_goal);
        } else if (
          [
            AlertReason.HighFidelityInsufficientGoal, 
            AlertReason.LowFidelityInsufficientGoal,
            AlertReason.InsufficientGoalProgress,
          ].some((alert) => interventionGroup?.alert_reason.includes(alert))
        ) {
          isShowAlertForStudent = interventionGroup?.student_goals?.some(
            (goal) => goal.student.id === student.id && goal.insufficient_goal_progress
          );
        }
      }
      return !isReadonly && 
      interventionGroup.alert_reason &&
      (interventionGroup.alert_reason != AlertReason.None) &&
      (isShowAlertForStudent);
    }
    , [isReadonly, interventionGroup.alert_reason]
  );

  const hasTime = useMemo(() => {
    return !!(
      interventionGroup.time_monday_start ||
      interventionGroup.time_tuesday_start ||
      interventionGroup.time_wednesday_start ||
      interventionGroup.time_thursday_start ||
      interventionGroup.time_friday_start
    );
  }, [interventionGroup]);

  const [
    showInterventionTimeOfDayPanel,
    setShowInterventionTimeOfDayPanel,
  ] = useState<boolean>(hasTime);

  const { role: userRole, isTeacher, isSupervisor } = useUserRole();

  const infoByStage = useMemo(() => {
    switch (stage) {
      case InterventionGroupStage.STAGE_PREPARATION:
        return (
          <LoadingIndicator/>
          // <PreparationStageInfo
          //   userRole={userRole}
          //   interventionGroup={interventionGroup}
          //   onInterventionSkip={() =>
          //     dispatch(
          //       patchInterventionGroup(interventionGroup.id!, {
          //         skipped_prep: true,
          //       })
          //     )
          //   }
          // />
        );
      case InterventionGroupStage.STAGE_IMPLEMENTATION:
        return (
          <ImplementationStageInfo
            userRole={userRole}
            interventionGroup={interventionGroup}
            onInterventionSkip={() =>
              dispatch(
                patchInterventionGroup(interventionGroup.id!, {
                  skipped_meeting: true,
                })
              )
            }
          />
        );
      case InterventionGroupStage.STAGE_MONITORING:
        return (
          <MonitoringStageInfo
            hideActions={
              hideChartActions ||
              isTeacher ||
              isSupervisor ||
              showArchivedGroups ||
              isReadonly ||
              isIntGrpIDsWithReadAccess
            }
            interventionGroup={interventionGroup}
            progressOverviewLoader={progressOverviewLoader}
            caseId= {caseId}
          />
        );
      default:
        return null;
    }
  }, [stage, interventionGroup, userRole, isReadonly, progressOverviewLoader]);

  const onSupportImplFeedbackModalOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(changeSelectedInterventionGroup(interventionGroup));
    dispatch(
      linkToMeetingTab(interventionGroup!.id!, MeetingType.SUPPORT_INTERVENTION)
    );
  };

  const onGroupDetailsShow = () => {
    if (!showArchivedGroups && !isReadonly) {
      dispatch(showInterventionPlanModal(interventionGroup));
      // dispatch(changeSelectedInterventionGroup(interventionGroup));
      // history.push(`/cases/groups/${interventionGroup.id}/student-goals`);
    }
  };

  return {
    stage: stage,
    hasAlert: hasAlert,
    hasTime: hasTime,
    showInterventionTimeOfDayPanel,
    setShowInterventionTimeOfDayPanel,
    infoByStage,
    onSupportImplFeedbackModalOpen,
    onGroupDetailsShow,
  };
};

export default useInterventionGroupItem;
